import React, { useState, useEffect } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import useOnclickOutside from 'react-cool-onclickoutside';
import ValidationTypes from 'lib/constants/validationTypes';
import ValidationStore, { addValidation } from 'lib/stores/validationStore';
import withValidations from 'lib/hocs/withValidations';
import ConfigStore from 'lib/stores/configStore';
import { handleChange } from 'lib/stores/formStore';
import { formatIdFromName } from 'lib/utility';

import FormLabel from './FormLabel';
import FormAddressSearchDetail from './FormAddressSearchDetail';

const FormAddressSearch = ({ name, label, id, inputClass, required }) => {
  const { countryCode } = ConfigStore.useState(state => state);
  const requestOptions = { requestOptions: { componentRestrictions: { country: [countryCode] } }, debounce: 300 };
  const { ready, value, suggestions: { status, data }, setValue, clearSuggestions } = usePlacesAutocomplete(requestOptions);
  const [addressComponentData, setAddressComponentData] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const requiredTypes = ['postal_code', 'administrative_area_level_1', 'route', 'street_number'];

      let valid = addressComponentData.length > 0;

      for (let i = 0; i < requiredTypes.length; i++) {
        const type = requiredTypes[i];
        const includesType = addressComponentData.some(component => component.types.includes(type));

        if (!includesType) {
          valid = false;
          break;
        }
      }

      ValidationStore.update(s => addValidation(s, 'address', valid));
    }, 200);

    return () => clearTimeout(timer);
  }, [addressComponentData]);

  useEffect(() => {
    if (value && addressComponentData.length === 0) return;

    setAddressComponentData([]);
  }, [value]);

  const ref = useOnclickOutside(() => {
    clearSuggestions();
  });

  const handleInput = e => {
    setValue(e.target.value);
  };

  const setAddressComponentValues = components => {
    const hostAttributeTypes = ['postal_code', 'locality', 'country'];
    const addressTypes = ['street_number', 'subpremise', 'route'];

    hostAttributeTypes.forEach(type => {
      const [result] = components.filter(component => component.types[0] === type);
      const typeNames = { postal_code: 'post_code', locality: 'city', country: 'country_code' };

      if (!result) return;

      // Turns into user.host_attributes.address_post_code for instance
      handleChange(result.long_name, `user.host_attributes.address_${typeNames[type]}`);
    });

    addressTypes.forEach(type => {
      const [result] = components.filter(component => component.types[0] === type);
      const typeNames = { street_number: 'street_no', subpremise: 'unit_no', route: 'street_name' };

      if (!result) return;

      // Turns into address.street_no for instance
      handleChange(result.long_name, `address.${typeNames[type]}`);
    });
  };

  const handleSelect = ({ description }) => () => {
    setValue(description, false);
    clearSuggestions();

    getGeocode({ address: description })
      .then(results => {
        const [result] = results;

        handleChange(result.formatted_address, 'user.host_attributes.address_formatted');
        setAddressComponentValues(result.address_components);
        setAddressComponentData(result.address_components);

        return getLatLng(result);
      })
      .then(({ lat, lng }) => {
        handleChange([lng, lat], 'user.host_attributes.coordinates');
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log('Error: ', error);
      });
  };

  const renderSuggestions = () => (
    data.map(suggestion => {
      const { place_id, structured_formatting: { main_text, secondary_text } } = suggestion;
      const classes = 'choices__item choices__item--choice choices__item--selectable';

      // TODO: Add keyboard listener to run the handle select for accessibility
      return (
        <div key={place_id} className={classes} onClick={handleSelect(suggestion)} aria-hidden="true">
          <strong>{main_text}</strong>
          {' '}
          <small>{secondary_text}</small>
        </div>
      );
    })
  );

  return (
    <div className="mb-3" ref={ref}>
      <FormLabel id={id || formatIdFromName(name)} label={label} required={required} />
      <div className="position-relative">
        <input
          value={value}
          onChange={handleInput}
          className={inputClass}
          id={id || formatIdFromName(name)}
          data-testid={id || formatIdFromName(name)}
          placeholder="Search..."
          disabled={!ready}
        />
        {status === 'OK' && (
          <div className="choices__list choices__list--dropdown visible">
            <div className="choices__list">
              {renderSuggestions()}
            </div>
          </div>
        )}
      </div>
      <FormAddressSearchDetail
        key={value}
        addressComponentData={addressComponentData}
        handleChange={handleChange}
      />
    </div>
  );
};

export default withValidations({
  type: ValidationTypes.CUSTOM,
  name: 'address',
  required: true,
})(FormAddressSearch);
